import { fetchDefaultPaymentMethod } from "@/api/billing";

// Saved-method preattach (paid-checkout foundation slice 2.1, D-010).
// Restores the legacy Angular behavior: an eligible returning customer's
// saved default method is attached to the draft at checkout entry, so the
// Payment row completes on its own. Every failure path degrades to the
// ordinary setup flow.

const getPenmateEnv = (): any => (window as any)?.Penmate?.env;

const parseRate = (value: unknown, fallback: number): number => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim()) {
    const parsed = Number.parseFloat(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return fallback;
};

// FNV-1a, mirroring the replay sampler's hash so partial rates are stable
// per user if a staged rate is ever used.
const hashToUnitInterval = (input: string): number => {
  let hash = 0x811c9dc5;
  for (let i = 0; i < input.length; i += 1) {
    hash ^= input.charCodeAt(i);
    hash = (hash + ((hash << 1) + (hash << 4) + (hash << 7) + (hash << 8) + (hash << 24))) >>> 0;
  }
  return hash / 0x100000000;
};

// In-code default 100%; the env value is a kill/lower lever only
// (operator-approved D-017 deviation: no staged ramp).
export const preattachRampRate = (): number => {
  const env = getPenmateEnv();
  const rate = parseRate(env?.preattachRampRate, 1);
  return Math.max(0, Math.min(1, rate));
};

const userField = (user: any, camelKey: string, snakeKey: string): unknown =>
  user?.[camelKey] ?? user?.raw?.[camelKey] ?? user?.[snakeKey] ?? user?.raw?.[snakeKey];

export const userSavesBilling = (user: any): boolean =>
  userField(user, "saveBilling", "save_billing") === true;

const getUserId = (user: any): string | null => {
  const id = user?.id ?? user?.raw?.id;
  return id == null ? null : String(id);
};

export type PreattachDecision = { eligible: boolean; reason: string };

export const evaluatePreattach = (args: {
  user: any;
  paymentRequired: boolean;
  hasPayment: boolean;
  isDemo: boolean;
}): PreattachDecision => {
  if (args.isDemo) return { eligible: false, reason: "demo" };
  if (!args.paymentRequired) return { eligible: false, reason: "payment_not_required" };
  if (args.hasPayment) return { eligible: false, reason: "payment_present" };
  if (!userSavesBilling(args.user)) return { eligible: false, reason: "save_billing_off" };
  const userId = getUserId(args.user);
  if (!userId) return { eligible: false, reason: "no_user" };
  const rate = preattachRampRate();
  if (rate <= 0) return { eligible: false, reason: "rate_zero" };
  if (rate < 1 && hashToUnitInterval(`pm_preattach|${userId}`) >= rate) {
    return { eligible: false, reason: "not_in_rate" };
  }
  return { eligible: true, reason: "eligible" };
};

// Mirror of the replay sampler's payment-return guard: a Stripe redirect
// resume is mid-attach for this very letter, and preattach must stay out
// of its way.
const PAYMENT_RETURN_PARAMS = [
  "session_id",
  "setup_intent",
  "setup_intent_client_secret",
  "payment_intent",
  "payment_intent_client_secret",
];

export const hasPaymentReturnParams = (): boolean => {
  if (typeof window === "undefined") return false;
  try {
    const url = new URL(window.location.href);
    return PAYMENT_RETURN_PARAMS.some((param) => url.searchParams.has(param));
  } catch {
    return false;
  }
};

// Server decides the method (the user's live Stripe default, with the
// long-standing first-card fallback). Null on any failure or missing id —
// the caller falls back to the ordinary setup path and never blocks.
export const fetchPreattachMethod = async (): Promise<any | null> => {
  try {
    const method = await fetchDefaultPaymentMethod();
    return method && typeof method === "object" && method.id ? method : null;
  } catch {
    return null;
  }
};
